import React, { useState, useEffect, useMemo, useCallback } from "react";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import ReviewMarksToggle from "./reviewMarksToggle";
import { getProductWithQualityAttributes } from "../../common/services/products/productService";
import { filterProductQualityAttributeValues } from "../../common/services/products/productUtilities";
import { getActiveQualityAttributeValueIdsByMarket } from "../../common/services/markets/qualityAttributeValueMarketAssignmentService";
import { getMarketWithAttributes } from "../../common/services/markets/marketService";
import { useTranslation } from "react-i18next";
import FontAwesome from "react-fontawesome";

const ReviewMarksTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const [tableState, setTableState] = useState({
    mappedMarks: [],
    market: {},
    product: null,
    isLoading: true,
  });

  const { marks, onSort, handleApprove, handleReject, canApproveMarks } = props;

  // Memoize default column definitions
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
      minWidth: 140,
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      autoHeaderHeight: false,
    }),
    []
  );

  // Map marks data to view model
  const mapToViewModel = useCallback((marks, market) => {
    if (!marks || !market) return [];

    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
    };

    const dateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return marks.map((mark) => {
      const marketAttributeIndex =
        market.marketAttributes &&
        market.marketAttributes.findIndex(
          (u) => u.priceBasisId === mark.priceBasisId
        );
      const currencySymbol =
        (marketAttributeIndex > -1 &&
          market.marketAttributes[marketAttributeIndex].currencySymbol) ??
        "$";
      const pricePrecision =
        (marketAttributeIndex > -1 &&
          market.marketAttributes[marketAttributeIndex].pricePrecision) ??
        "2";

      return {
        markId: mark.markId,
        organizationName: mark.organizationName,
        marketDate: new Date(mark.marketDate).toLocaleDateString(
          "en-US",
          dateFormatOptions
        ),
        priceBasisName: mark.priceBasisName,
        termName: mark.termName,
        price:
          mark.price &&
          currencySymbol + Number(mark.price).toFixed(pricePrecision),
        bid:
          mark.bid && currencySymbol + Number(mark.bid).toFixed(pricePrecision),
        offer:
          mark.offer &&
          currencySymbol + Number(mark.offer).toFixed(pricePrecision),
        open:
          mark.open &&
          currencySymbol + Number(mark.open).toFixed(pricePrecision),
        high:
          mark.high &&
          currencySymbol + Number(mark.high).toFixed(pricePrecision),
        low:
          mark.low && currencySymbol + Number(mark.low).toFixed(pricePrecision),
        close:
          mark.close &&
          currencySymbol + Number(mark.close).toFixed(pricePrecision),
        override: mark.override,
        updatedDate: new Date(mark.updatedDate).toLocaleDateString(
          "en-US",
          dateTimeFormatOptions
        ),
        markState: mark.markState,
        ...mark.qualityAttributeValues.reduce((acc, attribute) => {
          acc[attribute.qualityAttributeId] = attribute.value;
          return acc;
        }, {}),
      };
    });
  }, []);

  // Memoize column definitions
  const columnDefs = useMemo(() => {
    if (!tableState.product) return [];

    return [
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        filter: false,
      },
      {
        field: "organizationName",
        headerName: t("SubmittingOrganizationName"),
        filter: false,
        minWidth: 150,
      },
      ...tableState.product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          field: qualityAttribute.qualityAttributeId,
          headerName: qualityAttribute.qualityAttributeName,
          filter: false,
        })),
      {
        field: "marketDate",
        headerName: t("MarketDate"),
        filter: false,
      },
      { field: "termName", headerName: t("Term"), filter: false },
      {
        field: "priceBasisName",
        headerName: t("PriceBasis"),
        filter: false,
      },
      { field: "price", headerName: t("Price"), filter: false },
      { field: "bid", headerName: t("Bid"), filter: false },
      { field: "offer", headerName: t("Offer"), filter: false },
      { field: "open", headerName: t("Open"), filter: false },
      { field: "high", headerName: t("High"), filter: false },
      { field: "low", headerName: t("Low"), filter: false },
      { field: "close", headerName: t("Close"), filter: false },
      {
        field: "override",
        headerName: t("Override"),
        filter: false,
        cellRenderer: (params) => (
          <FontAwesome name={params.data.override ? "check" : ""} />
        ),
      },
      {
        field: "markState",
        headerName: "",
        cellClass: "review-marks-action-cell",
        cellRenderer: (params) => (
          <ReviewMarksToggle
            markId={params.data.markId}
            markState={params.data.markState}
            handleApprove={handleApprove}
            handleReject={handleReject}
            canApproveMarks={canApproveMarks}
          />
        ),
        minWidth: 170,
        pinned: "right",
        lockPinned: true,
        filter: false,
      },
    ];
  }, [tableState.product, t, handleApprove, handleReject, canApproveMarks]);

  const autoSizeGridColumns = useCallback((params) => {
    params?.columnApi?.autoSizeAllColumns?.(false);
    params?.api?.autoSizeAllColumns?.(false);
  }, []);

  // Initialize the table
  useEffect(() => {
    const initializeTable = async () => {
      if (!marks || marks.length === 0) {
        setTableState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const productId = marks[0].productId;
        const marketId = marks[0].marketId;
        const marketDate = new Date().toLocaleDateString("fr-CA");
        const [product, activeQualityAttributeValueIds, market] =
          await Promise.all([
            getProductWithQualityAttributes(productId),
            getActiveQualityAttributeValueIdsByMarket(marketId, marketDate),
            getMarketWithAttributes(marketId),
          ]);

        // Filter product's qualityAttributeValues to only include those with active market assignments
        const filteredProduct = filterProductQualityAttributeValues(
          product,
          activeQualityAttributeValueIds
        );

        const mappedMarks = mapToViewModel(marks, market);

        setTableState({
          mappedMarks,
          market,
          product: filteredProduct,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error initializing review marks table:", error);
        setTableState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    initializeTable();
  }, [marks, mapToViewModel]);

  // Update marks when they change
  useEffect(() => {
    if (tableState.market && marks !== tableState.originalMarks) {
      const mappedMarks = mapToViewModel(marks, tableState.market);
      setTableState((prev) => ({
        ...prev,
        mappedMarks,
        originalMarks: marks,
      }));
    }
  }, [marks, tableState.market, tableState.originalMarks, mapToViewModel]);

  if (tableState.isLoading) return <Loading />;

  return (
    <div>
      <ThemeableAgGrid
        className="table-bold"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoSizeStrategy={{
          type: "fitCellContents",
          skipHeader: false,
          defaultMinWidth: 140,
        }}
        rowData={tableState.mappedMarks}
        onFirstDataRendered={autoSizeGridColumns}
        onRowDataUpdated={autoSizeGridColumns}
        onGridSizeChanged={autoSizeGridColumns}
        onSortChanged={onSort}
        animateRows={true}
        getRowId={(params) => params.data.markId.toString()}
        domLayout="autoHeight"
        allowHorizontalScroll={true}
      />
    </div>
  );
};

export default ReviewMarksTable;
